import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Avatar, Button, InlineError } from '../../components/ui';
import { EmojiButton } from '../comments/CommentInsertPickers';
import { CommentBody } from '../comments/CommentBody';
import { CommentReactions, QUICK_REACTIONS, reactionName } from '../comments/CommentReactions';
import { AnimatedEmoji } from '../comments/AnimatedEmoji';
import { COMMENT_EMOJI } from '../comments/emojiSet';
import { NOTO_CREDIT } from '../comments/notoEmoji';
import type { ReactionSummary } from '../comments/reactions';
import { consoleReactionError, consoleReactionWho } from './consoleReactions';
import { HuddleSpeakButton, HuddleSpeakFeedback } from './HuddleSpeak';
import { useHuddleDictation } from './useHuddleDictation';
import { huddleCommentHtml, huddleCommentTime } from './huddlePresentation';
import {
  HuddleError,
  type ConsoleMessage,
  type ConsoleMessages,
  type ConsoleQuote,
  type ConsoleSearch,
} from './huddleApi';
import {
  compareMessages,
  jumpToMessage,
  mergeMessages,
  quoteExcerpt,
  searchReady,
  type MessageCursor,
} from './consoleConversationState';
import './consoleConversation.css';
import { ConsoleVoiceMessage } from './ConsoleVoiceMessage';
import type { ConsoleVoiceAccess } from './huddleApi';

export function ConsoleQuoteBlock({
  quote,
  onJump,
}: {
  quote: ConsoleQuote | null | undefined;
  onJump: () => void;
}) {
  return quote?.available ? (
    <button
      type="button"
      className="console-quote"
      onClick={onJump}
      aria-label={`Jump to original message by ${quote.author}`}
    >
      <strong>{quote.author}</strong>
      <span>{quote.excerpt}</span>
    </button>
  ) : (
    <div className="console-quote">Original unavailable</div>
  );
}
interface Props {
  voiceAccess?: ConsoleVoiceAccess;
  initialMessageId?: string;
  open: boolean;
  title: string;
  closeLabel: string;
  phone: boolean;
  onClose: () => void;
  read: (cursor?: MessageCursor) => Promise<ConsoleMessages>;
  search: (query: string, cursor?: MessageCursor) => Promise<ConsoleSearch>;
  markRead: () => Promise<unknown> | void;
  send: (body: string, source: string | null) => Promise<unknown>;
  badge?: string | null;
  /**
   * Emoji reactions (20270242000100). Left out, the chat has no reaction
   * controls at all. read answers null when this chat cannot take reactions.
   */
  reactions?: {
    partner: string;
    read: (ids: string[]) => Promise<Map<string, ReactionSummary[]> | null>;
    set: (id: string, emoji: string, on: boolean) => Promise<unknown>;
  };
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
/** One instance per identity/link (parents key it); no bearer token in a cache key. */
export function ConsoleConversation(props: Props) {
  const { open, phone, title, closeLabel, badge, onClose } = props;
  const api = useRef(props);
  api.current = props;
  const [messages, setMessages] = useState<ConsoleMessage[]>([]);
  const rows = useRef<ConsoleMessage[]>([]);
  const more = useRef(false);
  const cursor = useRef<MessageCursor | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [quote, setQuote] = useState<ConsoleMessage | null>(null);
  const [sending, setSending] = useState(false);
  const sendLock = useRef(false);
  const [sendError, setSendError] = useState('');
  const [searching, setSearching] = useState(false);
  const searchingRef = useRef(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ConsoleSearch | null>(null);
  const [searchError, setSearchError] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);
  const searchOp = useRef(0);
  const submitted = useRef('');
  const [jumpStatus, setJumpStatus] = useState('');
  const jumpOp = useRef(0);
  const jumping = useRef(false);
  const target = useRef('');
  const [highlight, setHighlight] = useState('');
  const [returnId, setReturnId] = useState('');
  const [away, setAway] = useState(false);
  const following = useRef(true);
  const [newMessages, setNewMessages] = useState(false);
  const [action, setAction] = useState<ConsoleMessage | null>(null);
  const actionButton = useRef<HTMLButtonElement>(null);
  const actionReturn = useRef<HTMLElement | null>(null);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const holdStart = useRef({ x: 0, y: 0 });
  const list = useRef<HTMLDivElement>(null);
  const searchButton = useRef<HTMLButtonElement>(null);
  const anchor = useRef<{ height: number; top: number } | null>(null);
  const epoch = useRef(0);
  const refreshOp = useRef(0);
  const loaded = useRef(false);
  const marking = useRef('');
  const paging = useRef(false);
  const [reactionMap, setReactionMap] = useState<Map<string, ReactionSummary[]>>(new Map());
  const [reactable, setReactable] = useState(true);
  const [reactionBusy, setReactionBusy] = useState(false);
  const [reactionError, setReactionError] = useState('');
  const [allEmoji, setAllEmoji] = useState(false);
  const reactionOp = useRef(0);
  const reactionLock = useRef(false);
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          codeBlock: false,
          ...(phone
            ? {
                bulletList: false,
                orderedList: false,
                listItem: false,
                listKeymap: false,
                horizontalRule: false,
                bold: false,
                italic: false,
                underline: false,
                strike: false,
                code: false,
                link: false,
              }
            : {}),
        }),
      ],
      editorProps: {
        attributes: {
          class: 'comment-editor-surface',
          'aria-label': phone ? 'Message Kyu' : 'Message Alex',
        },
      },
    },
    [],
  );
  const dictation = useHuddleDictation(editor, sending || !open || searching);
  useEffect(() => {
    editor?.setEditable(!sending && !unavailable);
  }, [editor, sending, unavailable]);

  function publish(next: ConsoleMessage[], nextMore = more.current) {
    rows.current = next;
    more.current = nextMore;
    setMessages(next);
    setHasMore(nextMore);
    // Resolve the draft from fresh authorized rows too; never preserve recalled text.
    setQuote((current) =>
      current === null ? null : (next.find((row) => row.id === current.id) ?? null),
    );
  }
  function cancelJump() {
    jumpOp.current++;
    jumping.current = false;
    setJumpStatus('');
  }
  function loseAccess() {
    cancelJump();
    searchOp.current++;
    rows.current = [];
    cursor.current = undefined;
    setMessages([]);
    setQuote(null);
    setResults(null);
    setSearchBusy(false);
    setUnavailable(true);
    setHasMore(false);
  }
  function keepPosition() {
    if (list.current)
      anchor.current = { height: list.current.scrollHeight, top: list.current.scrollTop };
    following.current = false;
    setAway(true);
  }
  async function refresh() {
    const op = epoch.current;
    const request = ++refreshOp.current;
    try {
      const page = await api.current.read();
      if (op !== epoch.current || request !== refreshOp.current) return;
      const incoming = page.messages;
      const last = incoming.at(-1);
      // An authoritative newest page removes deleted rows in that range, while
      // preserving the displaced tail and every already loaded older page.
      const preserved = rows.current.filter(
        (row) => page.has_more && last && compareMessages(row, last) > 0,
      );
      const next = mergeMessages(preserved, incoming);
      if (rows.current[0] && incoming[0]?.id !== rows.current[0]?.id && !following.current)
        setNewMessages(true);
      if (!loaded.current) {
        cursor.current = last;
        more.current = page.has_more;
        loaded.current = true;
      }
      publish(next);
      setLoading(false);
      setReadError('');
      setUnavailable(false);
      // Re-read older loaded windows so edits, deletions and quote availability
      // never live forever in the client merge. This does not move the old cursor.
      const oldestLoaded = rows.current.at(-1);
      const boundaries = rows.current.filter(
        (_, i) => i >= 24 && i < rows.current.length - 1 && (i + 1) % 25 === 0,
      );
      for (const boundary of boundaries) {
        if (op !== epoch.current || request !== refreshOp.current) return;
        const older = await api.current.read(boundary);
        if (op !== epoch.current || request !== refreshOp.current) return;
        const tail = older.messages.at(-1);
        const retained = rows.current.filter(
          (row) =>
            compareMessages(row, boundary) <= 0 ||
            (older.has_more && tail && compareMessages(row, tail) > 0),
        );
        publish(
          mergeMessages(
            retained,
            older.messages.filter((row) => oldestLoaded && compareMessages(row, oldestLoaded) <= 0),
          ),
        );
      }
      const newest = next[0]?.id ?? 'empty';
      if (!searchingRef.current && !jumping.current && marking.current !== newest) {
        marking.current = newest;
        try {
          await api.current.markRead();
        } catch {
          marking.current = '';
        }
      }
    } catch (cause) {
      if (op !== epoch.current || request !== refreshOp.current) return;
      if (cause instanceof HuddleError && cause.kind === 'off') loseAccess();
      else setReadError('Chat could not be refreshed. Try again.');
      setLoading(false);
    }
  }
  useEffect(() => {
    if (!open) return;
    void refresh();
    const timer = window.setInterval(() => {
      if (
        document.visibilityState === 'visible' &&
        !paging.current &&
        !jumping.current &&
        !sendLock.current
      )
        void refresh();
    }, 15000);
    return () => {
      window.clearInterval(timer);
      epoch.current++;
      jumpOp.current++;
      searchOp.current++;
      jumping.current = false;
      clearTimeout(hold.current);
    };
  }, [open]);
  useLayoutEffect(() => {
    const box = list.current;
    if (!box || searching) return;
    if (anchor.current) {
      box.scrollTop = anchor.current.top + box.scrollHeight - anchor.current.height;
      anchor.current = null;
    } else if (following.current && !jumping.current) box.scrollTop = box.scrollHeight;
  }, [messages, searching, open]);
  useEffect(() => {
    if (!highlight) return;
    const el = list.current?.querySelector<HTMLElement>(`[data-message-id="${highlight}"]`);
    el?.scrollIntoView?.({ block: 'center', behavior: 'auto' });
    el?.focus({ preventScroll: true });
    const timer = window.setTimeout(() => setHighlight(''), 2000);
    return () => window.clearTimeout(timer);
  }, [highlight, messages, searching]);

  async function older() {
    if (paging.current || jumping.current || !cursor.current) return;
    paging.current = true;
    refreshOp.current++;
    const op = epoch.current;
    keepPosition();
    try {
      const page = await api.current.read(cursor.current);
      if (op !== epoch.current) return;
      if (page.messages.length === 0 && !page.has_more) {
        publish(rows.current, false);
        setReadError('');
        return;
      }
      const merged = mergeMessages(rows.current, page.messages);
      if (merged.length === rows.current.length || page.messages.at(-1)?.id === cursor.current.id)
        throw Error('no progress');
      cursor.current = page.messages.at(-1);
      publish(merged, page.has_more);
      setReadError('');
    } catch (cause) {
      if (op === epoch.current) {
        if (cause instanceof HuddleError && cause.kind === 'off') loseAccess();
        else setReadError('Could not load older messages. Try again.');
      }
    } finally {
      paging.current = false;
    }
  }
  async function jump(id: string, replyId = '') {
    const op = ++jumpOp.current;
    refreshOp.current++;
    const life = epoch.current;
    target.current = id;
    jumping.current = true;
    following.current = false;
    setAway(true);
    setSearching(false);
    searchingRef.current = false;
    searchOp.current++;
    setSearchBusy(false);
    setReturnId(replyId);
    setJumpStatus('Finding message…');
    try {
      const result = await jumpToMessage(
        id,
        rows.current,
        more.current,
        (c) => api.current.read(c),
        () => op !== jumpOp.current || life !== epoch.current,
        (next, hasMore) => {
          cursor.current = next.at(-1);
          publish(next, hasMore);
        },
      );
      if (op !== jumpOp.current || life !== epoch.current) return;
      if (result.status === 'found') {
        // A search result or cached source may have been deleted after the read.
        const index = result.messages.findIndex((row) => row.id === id);
        const checked = await api.current.read(index > 0 ? result.messages[index - 1] : undefined);
        if (op !== jumpOp.current || life !== epoch.current) return;
        if (!checked.messages.some((row) => row.id === id)) {
          publish(rows.current.filter((row) => row.id !== id));
          setJumpStatus('Message unavailable');
        } else {
          publish(mergeMessages(rows.current, checked.messages));
          setHighlight(id);
          setJumpStatus('');
        }
      } else
        setJumpStatus(
          result.status === 'ceiling'
            ? 'Keep looking'
            : result.status === 'stalled'
              ? 'Finding stopped. Retry.'
              : 'Message unavailable',
        );
    } catch (cause) {
      if (op === jumpOp.current && life === epoch.current) {
        if (cause instanceof HuddleError && cause.kind === 'off') loseAccess();
        else setJumpStatus('Could not find the message. Retry.');
      }
    } finally {
      if (op === jumpOp.current) jumping.current = false;
    }
  }
  function back() {
    searchOp.current++;
    setSearching(false);
    searchingRef.current = false;
    setSearchBusy(false);
    searchButton.current?.focus();
  }
  function openSearch() {
    cancelJump();
    searchingRef.current = true;
    setSearching(true);
    setAction(null);
  }
  async function runSearch(text: string, append = false) {
    if (!searchReady(text, 0, 250)) return;
    const op = ++searchOp.current;
    const life = epoch.current;
    const before = append ? (results?.next_cursor ?? undefined) : undefined;
    if (append && submitted.current !== text.trim()) return;
    submitted.current = text.trim();
    setSearchBusy(true);
    setSearchError('');
    try {
      const found = await api.current.search(text.trim(), before);
      if (op !== searchOp.current || life !== epoch.current || !searchingRef.current) return;
      if (!found.available) {
        loseAccess();
        return;
      }
      if (append && found.has_more && found.next_cursor?.id === before?.id)
        throw Error('no progress');
      setResults((previous) => ({
        ...found,
        results: append ? mergeMessages(previous?.results ?? [], found.results) : found.results,
      }));
    } catch {
      if (op === searchOp.current) setSearchError('Search could not be loaded. Try again.');
    } finally {
      if (op === searchOp.current) setSearchBusy(false);
    }
  }
  useEffect(() => {
    if (!searching) return;
    const changed = Date.now();
    const timer = window.setTimeout(() => {
      if (searchReady(query, changed, Date.now())) void runSearch(query);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query, searching]);
  function changeQuery(value: string) {
    searchOp.current++;
    setQuery(value);
    setResults(null);
    setSearchError('');
    setSearchBusy(false);
  }
  async function send() {
    if (sendLock.current || unavailable) return;
    cancelJump();
    dictation.cancel();
    const body = phone
      ? (editor?.getText({ blockSeparator: '\n' }) ?? '')
      : (editor?.getHTML() ?? '');
    if (editor?.isEmpty || !body.trim()) return;
    sendLock.current = true;
    setSending(true);
    setSendError('');
    const life = epoch.current;
    try {
      await api.current.send(body, quote?.id ?? null);
      // This instance belongs to one identity; a successful send clears even
      // if its chat was closed while the request was pending.
      editor?.commands.clearContent();
      setQuote(null);
      if (life !== epoch.current) return;
      following.current = true;
      setAway(false);
      setNewMessages(false);
      await refresh();
    } catch (cause) {
      if (life !== epoch.current) return;
      setSendError(
        cause instanceof HuddleError && cause.kind === 'rate'
          ? 'You have sent a lot in the last hour. Try again in a little while.'
          : phone
            ? 'That message did not send. Check your connection and try again.'
            : 'That message did not send. Try again.',
      );
    } finally {
      sendLock.current = false;
      setSending(false);
    }
  }
  // Chips are extra: a failed read keeps the last chips and never costs a message.
  async function loadReactions() {
    const source = api.current.reactions;
    if (!source) return;
    const op = ++reactionOp.current;
    const life = epoch.current;
    try {
      const map = await source.read(rows.current.map((row) => row.id));
      if (op !== reactionOp.current || life !== epoch.current) return;
      setReactable(map !== null);
      setReactionMap(map ?? new Map());
    } catch {
      // Keep what is shown; the next poll tries again.
    }
  }
  useEffect(() => {
    if (open && messages.length > 0) void loadReactions();
  }, [messages, open]);
  async function react(id: string, emoji: string, mine: boolean) {
    const source = api.current.reactions;
    if (!source || reactionLock.current) return;
    reactionLock.current = true;
    setReactionBusy(true);
    setReactionError('');
    const life = epoch.current;
    try {
      await source.set(id, emoji, !mine);
    } catch (cause) {
      if (life === epoch.current)
        setReactionError(consoleReactionError(cause instanceof Error ? cause.message : ''));
    } finally {
      reactionLock.current = false;
      if (life === epoch.current) setReactionBusy(false);
    }
    if (life === epoch.current) await loadReactions();
  }
  function pickReaction(message: ConsoleMessage, emoji: string) {
    closeMenu();
    const mine = reactionMap.get(message.id)?.some((r) => r.emoji === emoji && r.mine) === true;
    void react(message.id, emoji, mine);
  }
  function reply(message: ConsoleMessage) {
    setQuote(message);
    setAction(null);
    editor?.commands.focus();
  }
  function menu(message: ConsoleMessage) {
    actionReturn.current = document.activeElement as HTMLElement;
    setAllEmoji(false);
    setAction(message);
  }
  function closeMenu() {
    setAction(null);
    actionReturn.current?.focus();
  }
  useEffect(() => {
    if (action) actionButton.current?.focus();
  }, [action]);
  function latest() {
    cancelJump();
    following.current = true;
    setAway(false);
    setNewMessages(false);
    setReturnId('');
    if (list.current) list.current.scrollTop = list.current.scrollHeight;
    void refresh();
  }
  useEffect(() => {
    if (open && props.initialMessageId) {
      const life = epoch.current;
      const id = props.initialMessageId;
      // A just-sent voice row is not in the cached page yet. Loading it first
      // avoids jump canceling the opening refresh against an empty list.
      void refresh().then(() => {
        if (life === epoch.current) void jump(id);
      });
    }
  }, [open, props.initialMessageId]);
  if (!open) return null;
  return (
    <div
      className="console-conversation"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          if (action) closeMenu();
          else if (searching) back();
          else if (jumping.current) cancelJump();
          else {
            dictation.cancel();
            onClose();
          }
        }
      }}
    >
      <header className={phone ? 'console-conversation-head' : 'my-day-console-chat-head'}>
        <h2>{title}</h2>
        {badge ? <span aria-label={`${badge} unread`}>{badge}</span> : null}
        <button
          ref={searchButton}
          type="button"
          aria-label="Search messages"
          onClick={openSearch}
          disabled={unavailable}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="m15 15 6 6" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
        <button
          type="button"
          aria-label={closeLabel}
          onClick={() => {
            dictation.cancel();
            onClose();
          }}
        >
          ×
        </button>
      </header>
      {unavailable ? (
        <p role="status">This chat is unavailable. Reopen it with a current session or link.</p>
      ) : null}
      {searching ? (
        <section className="console-search" aria-label="Search messages">
          <button type="button" onClick={back}>
            ‹ Back to chat
          </button>
          <h3>Search messages</h3>
          <p>Voice transcripts are not searched yet</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void runSearch(query);
            }}
          >
            <input
              autoFocus
              type="search"
              aria-label="Search all messages in this chat"
              maxLength={200}
              value={query}
              onChange={(e) => changeQuery(e.target.value)}
            />
            <button type="button" aria-label="Clear search" onClick={() => changeQuery('')}>
              ×
            </button>
          </form>
          <p>All messages in this chat</p>
          <div role="status">
            {searchBusy
              ? 'Searching…'
              : query.trim().length < 2
                ? 'Type at least 2 characters'
                : results?.results.length === 0
                  ? 'No messages found.'
                  : ''}
          </div>
          {searchError ? (
            <>
              <InlineError>{searchError}</InlineError>
              <button type="button" onClick={() => void runSearch(query)}>
                Retry search
              </button>
            </>
          ) : null}
          <div className="console-search-results">
            {results?.results.map((result) => (
              <button type="button" key={result.id} onClick={() => void jump(result.id)}>
                <strong>{result.author}</strong>
                <time>{huddleCommentTime(result.created_at)}</time>
                <span>{result.excerpt}</span>
              </button>
            ))}
          </div>
          {results?.has_more ? (
            <button type="button" disabled={searchBusy} onClick={() => void runSearch(query, true)}>
              More results
            </button>
          ) : null}
        </section>
      ) : (
        <>
          <div
            className={phone ? 'console-message-list' : 'my-day-console-chat-list'}
            ref={list}
            onScroll={() => {
              const el = list.current;
              if (el) {
                following.current = el.scrollHeight - el.scrollTop - el.clientHeight < 64;
                setAway(!following.current);
              }
            }}
          >
            {hasMore ? (
              <button type="button" onClick={() => void older()}>
                Show older messages
              </button>
            ) : null}
            {[...messages].reverse().map((message) => (
              <article
                tabIndex={-1}
                data-message-id={message.id}
                key={message.id}
                className={`${phone ? 'console-message' : 'my-day-console-message'}${message.mine ? (phone ? ' console-message--mine' : ' my-day-console-message--mine') : ''}${highlight === message.id ? ' console-message-highlight' : ''}`}
                onPointerDown={(e) => {
                  if (e.pointerType === 'mouse' || (e.target as Element).closest('button,a,input'))
                    return;
                  holdStart.current = { x: e.clientX, y: e.clientY };
                  clearTimeout(hold.current);
                  hold.current = setTimeout(() => menu(message), 450);
                }}
                onPointerMove={(e) => {
                  if (
                    Math.abs(e.clientX - holdStart.current.x) > 10 ||
                    Math.abs(e.clientY - holdStart.current.y) > 10
                  )
                    clearTimeout(hold.current);
                }}
                onPointerUp={() => clearTimeout(hold.current)}
                onPointerCancel={() => clearTimeout(hold.current)}
                onContextMenu={(e) => {
                  if (phone || action !== null) e.preventDefault();
                }}
              >
                {!phone ? (
                  <Avatar
                    name={message.author || 'Someone'}
                    imageUrl={message.author_avatar_url}
                    size="sm"
                  />
                ) : null}
                <div className="console-message-content">
                  <div className="console-message-meta">
                    <strong>{message.author}</strong>
                    <button
                      className="console-message-more"
                      type="button"
                      aria-label={`More actions for message by ${message.author}`}
                      onClick={() => menu(message)}
                    >
                      ⋯
                    </button>
                  </div>
                  {message.quoted_comment_id ? (
                    <ConsoleQuoteBlock
                      quote={message.quote}
                      onJump={() => void jump(message.quoted_comment_id!, message.id)}
                    />
                  ) : null}
                  {message.body === '<p>Voice note</p>' && props.voiceAccess ? (
                    <ConsoleVoiceMessage id={message.id} access={props.voiceAccess} />
                  ) : phone ? (
                    <div dangerouslySetInnerHTML={{ __html: huddleCommentHtml(message.body) }} />
                  ) : (
                    <CommentBody html={message.body} />
                  )}
                  <div className="console-message-foot">
                    <time dateTime={message.created_at}>
                      {huddleCommentTime(message.created_at)}
                    </time>
                    {props.reactions ? (
                      <CommentReactions
                        reactions={reactionMap.get(message.id)}
                        canReact={reactable && !unavailable}
                        pending={reactionBusy}
                        onToggle={(emoji, mine) => void react(message.id, emoji, mine)}
                        onAdd={() => menu(message)}
                        addLabel={`React to message by ${message.author}`}
                        describe={(reaction) =>
                          `${capitalized(reactionName(reaction.emoji))}: ${consoleReactionWho(reaction, props.reactions?.partner ?? 'Someone')}`
                        }
                      />
                    ) : null}
                    <button
                      className="console-reply-action"
                      type="button"
                      aria-label={`Reply to message by ${message.author}`}
                      onClick={() => reply(message)}
                    >
                      Reply
                    </button>
                  </div>
                </div>
              </article>
            ))}
            {loading ? <p>Loading chat…</p> : null}
          </div>
          {readError ? (
            <div role="alert">
              {readError}{' '}
              <button
                type="button"
                onClick={() =>
                  void (readError.startsWith('Could not load older') ? older() : refresh())
                }
              >
                Retry
              </button>
            </div>
          ) : null}
          {jumpStatus ? (
            <div role="status">
              {jumpStatus}{' '}
              {jumping.current ? (
                <button type="button" onClick={cancelJump}>
                  Cancel
                </button>
              ) : jumpStatus !== 'Message unavailable' ? (
                <button type="button" onClick={() => void jump(target.current)}>
                  {jumpStatus === 'Keep looking' ? 'Keep looking' : 'Retry'}
                </button>
              ) : null}
            </div>
          ) : null}
          {away ? (
            <div className="console-latest">
              <button type="button" onClick={latest}>
                {newMessages ? 'New messages' : 'Back to latest'}
              </button>
              {returnId ? (
                <button type="button" onClick={() => void jump(returnId)}>
                  Back to reply
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
      <div
        className={phone ? 'huddle-compose' : 'my-day-console-compose'}
        hidden={searching || unavailable}
      >
        {phone ? <p className="huddle-posting-as">Posting as Alex</p> : null}
        {quote ? (
          <div className="console-draft-quote">
            <div>
              <strong>Replying to {quote.author}</strong>
              <span>{quoteExcerpt(quote.body)}</span>
            </div>
            <button
              type="button"
              disabled={sending}
              aria-label="Remove quoted message"
              onClick={() => setQuote(null)}
            >
              ×
            </button>
          </div>
        ) : null}
        <div className="comment-editor">
          <div className="comment-editor-toolbar">
            <EmojiButton
              disabled={sending}
              onPick={(emoji) =>
                editor?.chain().focus().insertContent({ type: 'text', text: emoji }).run()
              }
            />
            <HuddleSpeakButton dictation={dictation} label="Dictate a message" disabled={sending} />
          </div>
          <EditorContent editor={editor} />
          <HuddleSpeakFeedback dictation={dictation} />
        </div>
        {reactionError ? <InlineError>{reactionError}</InlineError> : null}
        {sendError ? <InlineError>{sendError}</InlineError> : null}
        <Button onClick={() => void send()} disabled={sending || unavailable}>
          {sending ? 'Sending…' : sendError ? 'Try again' : 'Send'}
        </Button>
      </div>
      {action ? (
        <div className="console-actions-backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Message actions"
            className="console-actions"
            onKeyDown={(e) => {
              if (e.key === 'Tab') {
                e.preventDefault();
                const buttons = [
                  ...e.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'),
                ];
                const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
                const next = e.shiftKey
                  ? at <= 0
                    ? buttons.length - 1
                    : at - 1
                  : (at + 1) % buttons.length;
                buttons[next]?.focus();
              }
            }}
          >
            <h3>Message actions</h3>
            <p>{quoteExcerpt(action.body)}</p>
            {props.reactions && reactable && !unavailable ? (
              <>
                <div
                  role="group"
                  aria-label="React"
                  className="console-actions-react"
                  title={NOTO_CREDIT}
                >
                  {QUICK_REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      aria-label={`React ${reactionName(emoji)}`}
                      aria-pressed={
                        reactionMap.get(action.id)?.some((r) => r.emoji === emoji && r.mine) ===
                        true
                      }
                      disabled={reactionBusy}
                      onClick={() => pickReaction(action, emoji)}
                    >
                      <AnimatedEmoji emoji={emoji} />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="console-actions-more-emoji"
                  aria-expanded={allEmoji}
                  onClick={() => setAllEmoji((was) => !was)}
                >
                  More emoji
                </button>
                {allEmoji ? (
                  <div className="comment-emoji-grid console-actions-emoji">
                    {COMMENT_EMOJI.map((emoji) => (
                      <button
                        key={emoji.char}
                        type="button"
                        className="comment-emoji"
                        aria-label={emoji.name}
                        title={emoji.name}
                        disabled={reactionBusy}
                        onClick={() => pickReaction(action, emoji.char)}
                      >
                        {emoji.char}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
            <button type="button" ref={actionButton} onClick={() => reply(action)}>
              Reply
            </button>
            <button type="button" onClick={closeMenu}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
